import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Apple, Play, Monitor, Copy, Check } from "lucide-react";
import { Mole } from "../components/Mole";
import { Reveal } from "../components/Reveal";
import { TelegramShareButton } from "../components/TelegramShareButton";

const INVITE_CONFIG = {
  appStoreUrl: "",
  googlePlayUrl: "",
  windowsUrl: "",
  bonusDays: 3,
  ofertaUrl: "/terms",
};

const PLATFORMS = [
  { id: "appstore", icon: Apple, label: "App Store", url: INVITE_CONFIG.appStoreUrl },
  { id: "googleplay", icon: Play, label: "Google Play", url: INVITE_CONFIG.googlePlayUrl },
  { id: "windows", icon: Monitor, label: "Windows", url: INVITE_CONFIG.windowsUrl },
];

const CODE_RE = /^[A-Za-z0-9]{4,16}$/;

const detectPlatform = () => {
  if (typeof navigator === "undefined") return "windows";
  const ua = navigator.userAgent;
  if (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  )
    return "appstore";
  if (/android/i.test(ua)) return "googleplay";
  return "windows";
};

const Shot = ({ src, alt, caption, testId }) => {
  const [err, setErr] = useState(false);
  return (
    <figure data-testid={testId} className="w-full max-w-[280px]">
      {err ? (
        <div className="flex aspect-[3/5] items-center justify-center rounded-3xl border-2 border-dashed border-nora-ink/15 bg-nora-bg p-4 text-center text-xs text-nora-ink/40">
          Скриншот появится здесь
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onError={() => setErr(true)}
          className="w-full rounded-3xl shadow-lg shadow-nora-ink/10"
        />
      )}
      <figcaption className="mt-2 text-center text-xs leading-relaxed text-nora-ink/50">
        {caption}
      </figcaption>
    </figure>
  );
};

export default function Invite() {
  const { code: raw } = useParams();
  const clean = (raw || "").replace(/\/+$/, "").trim();
  const valid = CODE_RE.test(clean);
  const code = valid ? clean.toUpperCase() : null;

  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState(false);
  const [accepted, setAccepted] = useState(() => {
    try {
      return localStorage.getItem("nora-offer-accepted") === "1";
    } catch {
      return false;
    }
  });
  const toggleAccept = () =>
    setAccepted((v) => {
      const next = !v;
      try {
        localStorage.setItem("nora-offer-accepted", next ? "1" : "0");
      } catch {}
      return next;
    });
  const codeInputRef = useRef(null);
  const platform = useMemo(detectPlatform, []);
  const ordered = [
    ...PLATFORMS.filter((p) => p.id === platform),
    ...PLATFORMS.filter((p) => p.id !== platform),
  ];

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Тебе дарят 3 дня НОРЫ бесплатно";
    const defs = [
      ["name", "robots", "noindex, nofollow"],
      ["property", "og:title", "Тебе дарят 3 дня НОРЫ бесплатно"],
      ["property", "og:description", "VPN в одну кнопку. Скачай приложение и введи код друга."],
      ["name", "twitter:card", "summary_large_image"],
    ];
    const added = [];
    const updated = [];
    defs.forEach(([attr, key, val]) => {
      let m = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (m) {
        updated.push([m, m.getAttribute("content")]);
      } else {
        m = document.createElement("meta");
        m.setAttribute(attr, key);
        m.dataset.inviteMeta = "1";
        document.head.appendChild(m);
        added.push(m);
      }
      m.setAttribute("content", val);
    });
    return () => {
      document.title = prevTitle;
      added.forEach((m) => m.remove());
      updated.forEach(([m, prev]) => m.setAttribute("content", prev));
    };
  }, []);

  const copyCode = async () => {
    if (!code) return false;
    try {
      await navigator.clipboard.writeText(code);
      return true;
    } catch {
      codeInputRef.current?.select();
      return false;
    }
  };

  const onCopyClick = async () => {
    const ok = await copyCode();
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const onDownload = async (p) => {
    if (!p.url || !accepted) return;
    await copyCode();
    setNotice(true);
    setTimeout(() => setNotice(false), 2500);
    window.open(p.url, "_blank", "noopener,noreferrer");
  };

  const DownloadButton = ({ p, primary }) => {
    const Icon = p.icon;
    const empty = !p.url;
    const locked = !empty && !accepted;
    return (
      <button
        type="button"
        data-testid={`invite-download-${p.id}`}
        onClick={() => onDownload(p)}
        disabled={empty || locked}
        className={`inline-flex min-h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-full px-6 py-3.5 font-display text-sm font-bold transition-all ${
          empty
            ? "cursor-not-allowed border-2 border-nora-ink/10 text-nora-ink/35"
            : locked
              ? "cursor-not-allowed border-2 border-nora-ink/10 text-nora-ink/40 opacity-60"
              : primary
                ? "bg-nora-accent text-[#1F2730] shadow-lg shadow-nora-accent/25 hover:-translate-y-0.5 hover:bg-nora-accenthover"
                : "border-2 border-nora-ink/15 hover:-translate-y-0.5 hover:border-nora-accent hover:text-nora-accent"
        }`}
      >
        <Icon className={`h-5 w-5 ${empty ? "" : "text-current"}`} />
        {p.label}
        {empty && (
          <span className="rounded-full bg-nora-ink/10 px-2 py-0.5 text-xs">
            Скоро
          </span>
        )}
      </button>
    );
  };

  return (
    <main data-testid="invite-page">
      <section className="relative overflow-hidden pb-8 pt-28 sm:pt-36">
        <div
          aria-hidden="true"
          className="absolute -left-24 top-24 h-72 w-72 rounded-full bg-nora-pink/10 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="absolute -right-24 top-40 h-80 w-80 rounded-full bg-nora-mint/10 blur-3xl"
        />
        <div className="relative mx-auto grid max-w-5xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-[1.2fr_1fr]">
          <div className="text-center lg:text-left">
            {valid ? (
              <>
                <Reveal>
                  <p
                    data-testid="invite-eyebrow"
                    className="font-display text-xs font-bold uppercase tracking-[0.35em] text-nora-accent"
                  >
                    Тебя позвали в нору
                  </p>
                </Reveal>
                <Reveal delay={0.1}>
                  <h1
                    data-testid="invite-title"
                    className="mt-4 font-display text-3xl font-black leading-[1.12] tracking-tight sm:text-5xl"
                  >
                    Друг дарит тебе {INVITE_CONFIG.bonusDays} дня НОРЫ бесплатно
                  </h1>
                </Reveal>
                <Reveal delay={0.2}>
                  <p
                    data-testid="invite-subtitle"
                    className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-nora-ink/70 md:text-lg lg:mx-0"
                  >
                    НОРА это VPN в одну кнопку. Скачай приложение, введи код
                    друга и получи {INVITE_CONFIG.bonusDays} дня в подарок.
                  </p>
                </Reveal>
              </>
            ) : (
              <>
                <Reveal>
                  <h1
                    data-testid="invite-fallback-title"
                    className="font-display text-3xl font-black leading-[1.12] tracking-tight sm:text-5xl"
                  >
                    Ссылка-приглашение не сработала
                  </h1>
                </Reveal>
                <Reveal delay={0.1}>
                  <p
                    data-testid="invite-fallback-text"
                    className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-nora-ink/70 md:text-lg lg:mx-0"
                  >
                    Попроси друга прислать ссылку еще раз. А пока можно скачать
                    приложение: первый день бесплатно.
                  </p>
                </Reveal>
              </>
            )}
          </div>
          <Reveal
            delay={0.15}
            className="relative mx-auto w-full max-w-sm lg:max-w-md"
          >
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <Mole
                data-testid="invite-mascot"
                variant="burrow"
                className="w-full drop-shadow-2xl"
              />
            </motion.div>
          </Reveal>
        </div>
      </section>

      {valid ? (
        <section className="py-8 sm:py-12">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
              <Reveal>
                <div
                  data-testid="invite-step-1"
                  className="flex flex-col rounded-[2rem] border border-nora-ink/5 bg-nora-surface p-6 shadow-sm sm:p-8"
                >
                  <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-nora-ink/45">
                    Шаг 1
                  </p>
                  <h2 className="mt-2 font-display text-xl font-bold">
                    Скачай приложение
                  </h2>
                  <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-2xl border border-nora-ink/10 bg-nora-bg px-4 py-3.5 text-left">
                    <input
                      type="checkbox"
                      checked={accepted}
                      onChange={toggleAccept}
                      data-testid="invite-offer-checkbox"
                      className="mt-0.5 h-5 w-5 shrink-0 accent-nora-mint"
                    />
                    <span className="text-xs leading-relaxed text-nora-ink/60">
                      Согласен(на) с{" "}
                      <a
                        href="/terms"
                        className="font-semibold text-nora-accent hover:underline"
                      >
                        условиями оферты
                      </a>{" "}
                      и прошу открыть доступ сразу. Возврат средств проводит
                      App Store или Google Play по своим правилам.
                    </span>
                  </label>
                  <div className="mt-4 space-y-3">
                    {ordered.map((p, i) => (
                      <DownloadButton key={p.id} p={p} primary={i === 0} />
                    ))}
                  </div>
                  {notice && (
                    <p
                      data-testid="invite-download-notice"
                      className="mt-4 rounded-full bg-nora-mint/10 px-4 py-2 text-center text-sm font-semibold text-nora-mint"
                    >
                      Код скопирован. Вставь его в приложении
                    </p>
                  )}
                </div>
              </Reveal>

              <Reveal delay={0.12}>
                <div
                  data-testid="invite-step-2"
                  className="flex flex-col rounded-[2rem] border border-nora-ink/5 bg-nora-surface p-6 shadow-sm sm:p-8"
                >
                  <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-nora-ink/45">
                    Шаг 2
                  </p>
                  <h2 className="mt-2 font-display text-xl font-bold">
                    Скопируй код друга
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-nora-ink/60">
                    Это код твоего друга. Он был в ссылке, по которой ты пришел,
                    искать его нигде не нужно.
                  </p>
                  <input
                    ref={codeInputRef}
                    readOnly
                    value={code}
                    onFocus={(e) => e.target.select()}
                    onClick={(e) => e.target.select()}
                    data-testid="invite-code-input"
                    aria-label="Код друга"
                    className="mt-5 w-full rounded-2xl border-2 border-nora-accent/40 bg-nora-bg px-4 py-4 text-center font-mono text-2xl font-bold tracking-[0.2em] text-nora-ink focus:border-nora-accent focus:outline-none"
                  />
                  <motion.button
                    type="button"
                    whileTap={{ scale: 0.96 }}
                    onClick={onCopyClick}
                    data-testid="invite-copy-button"
                    className={`mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-full px-6 py-3.5 font-display text-sm font-bold transition-colors ${
                      copied
                        ? "bg-nora-mint text-white"
                        : "bg-nora-accent text-[#1F2730] shadow-lg shadow-nora-accent/25 hover:bg-nora-accenthover"
                    }`}
                  >
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    {copied ? "Скопировано" : "Скопировать код"}
                  </motion.button>
                </div>
              </Reveal>

              <Reveal delay={0.24} className="lg:col-span-2">
                <div
                  data-testid="invite-step-3"
                  className="rounded-[2rem] border border-nora-ink/5 bg-nora-surface p-6 shadow-sm sm:p-8"
                >
                  <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-nora-ink/45">
                    Шаг 3
                  </p>
                  <h2 className="mt-2 font-display text-2xl font-bold">
                    Вставь код в приложении
                  </h2>
                  <ol className="mt-5 list-decimal space-y-3 pl-5 text-base leading-relaxed text-nora-ink/75 marker:font-bold marker:text-nora-accent md:text-lg">
                    <li>Открой НОРУ и нажми «Настройки» внизу экрана.</li>
                    <li>
                      Нажми «Привязать Google» или «Привязать Apple» и войди в
                      свой аккаунт. Это нужно один раз, чтобы подарок
                      закрепился за тобой.
                    </li>
                    <li>Выбери «Позови друга».</li>
                    <li>
                      В блоке «Код друга» вставь код{" "}
                      <button
                        type="button"
                        onClick={onCopyClick}
                        data-testid="invite-code-chip"
                        title="Нажми, чтобы скопировать"
                        className="mx-1 inline-flex items-center gap-1.5 rounded-full bg-nora-accent/15 px-3 py-1 font-mono text-sm font-bold tracking-widest text-nora-accent transition-colors hover:bg-nora-accent/25"
                      >
                        {code}
                        <Copy className="h-3.5 w-3.5" />
                      </button>{" "}
                      и нажми «Активировать».
                    </li>
                  </ol>
                  <p
                    data-testid="invite-note-box"
                    className="mt-6 rounded-2xl border-2 border-nora-pink/25 bg-nora-pink/5 px-5 py-4 text-sm leading-relaxed text-nora-ink/65"
                  >
                    Не перепутай. На этом экране есть еще «Ваша ссылка»: она
                    твоя, чтобы звать своих друзей. Код друга вводится ниже, в
                    блоке «Код друга».
                  </p>
                  <div className="mt-5 flex flex-col items-center gap-5 sm:flex-row sm:items-start sm:justify-center">
                    <Shot
                      src="/app-step-1-settings.png"
                      alt="Экран настроек приложения НОРА, выделены строки привязки аккаунта и Позови друга"
                      caption="1. Привяжи Google или Apple. 2. Открой «Позови друга»"
                      testId="invite-shot-1"
                    />
                    <Shot
                      src="/app-step-2-code.png"
                      alt="Экран Позови друга в приложении НОРА, выделено поле Код друга и кнопка Активировать"
                      caption="Сюда вставь код друга и нажми «Активировать»"
                      testId="invite-shot-2"
                    />
                  </div>
                  <p className="mt-6 text-base leading-relaxed text-nora-ink/70">
                    {INVITE_CONFIG.bonusDays} дня начислятся сразу, как только
                    нажмешь «Активировать». Платить для этого не нужно. Без
                    привязки Google или Apple код не сработает. Один код на
                    аккаунт.
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-nora-ink/45">
                    {INVITE_CONFIG.bonusDays} дня по приглашению действуют вместо
                    стандартного пробного дня и не складываются с ним.{" "}
                    Подробности в{" "}
                    <a
                      href={INVITE_CONFIG.ofertaUrl}
                      className="font-semibold text-nora-accent hover:underline"
                    >
                      оферте
                    </a>
                    .
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-8 sm:py-12">
          <div className="mx-auto max-w-md px-4 sm:px-6">
            <Reveal>
              <div
                data-testid="invite-fallback-downloads"
                className="rounded-[2rem] border border-nora-ink/5 bg-nora-surface p-6 shadow-sm sm:p-8"
              >
                <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-nora-ink/10 bg-nora-bg px-4 py-3.5 text-left">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={toggleAccept}
                    data-testid="invite-offer-checkbox-fallback"
                    className="mt-0.5 h-5 w-5 shrink-0 accent-nora-mint"
                  />
                  <span className="text-xs leading-relaxed text-nora-ink/60">
                    Согласен(на) с{" "}
                    <a
                      href="/terms"
                      className="font-semibold text-nora-accent hover:underline"
                    >
                      условиями оферты
                    </a>{" "}
                    и прошу открыть доступ сразу. Возврат средств проводит App
                    Store или Google Play по своим правилам.
                  </span>
                </label>
                <div className="mt-4 space-y-3">
                  {ordered.map((p, i) => (
                    <DownloadButton key={p.id} p={p} primary={i === 0} />
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="py-10 sm:py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <Reveal>
            <div
              data-testid="invite-share-block"
              className="grid items-center gap-8 rounded-[2rem] border border-nora-mint/25 bg-nora-mint/10 p-8 sm:p-10 lg:grid-cols-[1.3fr_1fr]"
            >
              <div>
                <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
                  А теперь позови своих друзей
                </h2>
                <p className="mt-3 leading-relaxed text-nora-ink/70">
                  Каждому другу {INVITE_CONFIG.bonusDays} дня в подарок, тебе 5
                  дней за каждого. Дни придут, когда друг привяжет Google или
                  Apple и введет твой код.
                </p>
                <ol className="mt-5 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-nora-ink/75 marker:font-bold marker:text-nora-mint">
                  <li>Открой НОРУ: Настройки → Позови друга.</li>
                  <li>В блоке «Ваша ссылка» нажми значок копирования.</li>
                  <li>Отправь ссылку другу в любом мессенджере.</li>
                </ol>
                {valid && (
                  <TelegramShareButton
                    testId="invite-share-telegram"
                    className="mt-6 w-full sm:w-auto"
                    url={`${window.location.origin}/invite/${code}`}
                    text={`Тебе дарят ${INVITE_CONFIG.bonusDays} дня НОРЫ бесплатно — VPN в одну кнопку. Скачай приложение по ссылке и введи код друга.`}
                  />
                )}
              </div>
              <div className="flex justify-center">
                <Shot
                  src="/app-your-link.png"
                  alt="Экран Позови друга в приложении НОРА, выделен блок с вашей ссылкой"
                  caption="Твоя ссылка для друзей"
                  testId="invite-shot-3"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
